import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import Icono from "./Icono";
import { apilarDialogo, desapilarDialogo } from "./capaSuperior";
// Los errores se muestran como avisos emergentes (ver Avisos.tsx).
export { ErrorAviso } from "./Avisos";
export function Campo({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}
const aTexto = (n: number | null) => (n == null ? "" : String(n));
const aNumero = (t: string) =>
  t === "" || Number.isNaN(Number(t)) ? null : Number(t);
export function Numero({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = "any",
}: {
  label: string;
  value: number | null;
  onChange: (n: number | null) => void;
  min?: number;
  max?: number;
  step?: number | "any";
}) {
  // El input guarda su propio texto en vez de renderizar `value` directo.
  // Con `value={value ?? ""}` un campo en 0 no se podía vaciar: quien lo llama
  // hace `onChange={(n) => update({ campo: n ?? 0 })}`, así que borrarlo
  // devolvía 0 y el 0 reaparecía al instante. Escribir "100" encima dejaba
  // "0100" — y React tampoco lo corregía, porque compara el DOM contra la
  // prop con `!=` (floja) y `"0100" != 100` es false.
  const [texto, setTexto] = useState(() => aTexto(value));
  // Mientras el campo tiene el foco manda lo que el vendedor escribió, incluso
  // si está vacío. Solo se resincroniza con valores que llegan de afuera
  // (botón de tarifa de la ciudad, cargar una cotización del historial,
  // cotización nueva), que nunca ocurren con el campo enfocado.
  const enfocado = useRef(false);
  useEffect(() => {
    if (!enfocado.current && aNumero(texto) !== value) setTexto(aTexto(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <Campo label={label}>
      <input
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value);
          onChange(aNumero(e.target.value));
        }}
        onFocus={() => {
          enfocado.current = true;
        }}
        // Al salir se normaliza lo escrito: "0100" queda "100" y un campo
        // vacío vuelve a mostrar el valor real del modelo.
        onBlur={() => {
          enfocado.current = false;
          setTexto(aTexto(value));
        }}
      />
    </Campo>
  );
}
export function Porcentaje({
  label,
  value,
  onChange,
  max,
}: {
  label: string;
  value: number | null;
  onChange: (n: number | null) => void;
  max?: number;
}) {
  return (
    <Numero
      label={label}
      value={value == null ? null : Math.round(value * 10000) / 100}
      max={max}
      onChange={(n) => onChange(n == null ? null : n / 100)}
    />
  );
}
// Línea de estado bajo los botones de guardar/entregar: el tono (progreso,
// éxito, alerta) se deduce del texto para no cambiar cada setStatus().
export function EstadoOperacion({
  texto,
  fallo,
}: {
  texto: string;
  fallo: boolean;
}) {
  if (!texto) return null;
  const tono = fallo
    ? "alerta"
    : texto.endsWith("…")
      ? "progreso"
      : /cancelad/i.test(texto)
        ? "info"
        : "exito";
  const icono = {
    alerta: "alerta",
    progreso: "cargar",
    info: "info",
    exito: "check",
  } as const;
  return (
    <p role="status" className="notice estado-operacion" data-tono={tono}>
      <Icono nombre={icono[tono]} />
      <span>{texto}</span>
    </p>
  );
}
export function Modal({
  title,
  onClose,
  children,
  className,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null),
    titleId = useId();
  const returnFocus = useRef(document.activeElement);
  const [closing, setClosing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  function cerrar() {
    if (timer.current !== undefined) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onClose();
      return;
    }
    setClosing(true);
    timer.current = setTimeout(onClose, 160);
  }
  useEffect(() => {
    const dialog = ref.current!;
    dialog.showModal();
    apilarDialogo(dialog);
    return () => {
      clearTimeout(timer.current);
      desapilarDialogo(dialog);
      dialog.close();
      if (
        returnFocus.current instanceof HTMLElement &&
        returnFocus.current.isConnected
      )
        returnFocus.current.focus({ preventScroll: true });
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={className}
      aria-labelledby={titleId}
      data-closing={closing || undefined}
      onCancel={(e) => {
        e.preventDefault();
        cerrar();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) cerrar();
      }}
    >
      <div className="modal-content">
        <header className="row between">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="icon-button ghost"
            aria-label="Cerrar"
            onClick={cerrar}
          >
            <Icono nombre="cerrar" />
          </button>
        </header>
        {children}
      </div>
    </dialog>
  );
}
export function Paginacion({
  page,
  total,
  size = 20,
  onChange,
}: {
  page: number;
  total: number;
  size?: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="row between paginacion">
      <button disabled={page === 0} onClick={() => onChange(page - 1)}>
        <Icono nombre="anterior" /> Anterior
      </button>
      <span>
        {total} resultados · Página {page + 1} de{" "}
        {Math.max(1, Math.ceil(total / size))}
      </span>
      <button
        disabled={(page + 1) * size >= total}
        onClick={() => onChange(page + 1)}
      >
        Siguiente <Icono nombre="siguiente" />
      </button>
    </div>
  );
}
